/**
 * Converts an object to a URL query string with proper encoding and edge case handling.
 * Uses URLSearchParams for standard encoding with additional support for arrays and complex types.
 *
 * @param obj - The object to convert to a query string
 * @param options - Configuration options for query string generation
 * @returns A properly encoded query string without the leading '?'
 *
 * @example
 * objectToQuery({ name: 'John Doe', age: 30, active: true })
 * // Returns: "name=John%20Doe&age=30&active=true"
 *
 * @example
 * objectToQuery({ tags: ['a', 'b', 'c'] }, { arrayFormat: 'brackets' })
 * // Returns: "tags[]=a&tags[]=b&tags[]=c"
 *
 * @example
 * objectToQuery({ empty: '', nullVal: null }, { skipNull: false, skipEmptyString: false })
 * // Returns: "empty=&nullVal=null"
 */
export const objectToQuery = (
  obj: { [key: string]: unknown },
  options: {
    skipNull?: boolean;
    skipEmptyString?: boolean;
    arrayFormat?: 'comma' | 'repeat' | 'brackets';
  } = {},
): string => {
  // Default options
  const {
    skipNull = true,
    skipEmptyString = true,
    arrayFormat = 'comma',
  } = options;

  // Edge case: Handle null, undefined, or non-object inputs
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return '';
  }

  // Use URLSearchParams for automatic encoding
  const params = new URLSearchParams();

  Object.entries(obj).forEach(([key, value]) => {
    // Skip null and undefined values if configured
    if (value === null || value === undefined) {
      if (skipNull) return;
      // Assign null or undefined as query value if skipNull is false
      // Example: key=null or key=undefined
      params.append(key, value === null ? 'null' : 'undefined');
      return;
    }

    // Handle arrays with different formatting options
    if (Array.isArray(value)) {
      // Skip empty arrays
      if (value.length === 0) return;

      if (arrayFormat === 'repeat') {
        // Format: key=val1&key=val2&key=val3
        value.forEach((item) => {
          if (item !== null && item !== undefined) {
            params.append(key, String(item));
          }
        });
      } else if (arrayFormat === 'brackets') {
        // Format: key[]=val1&key[]=val2&key[]=val3
        // Note: URLSearchParams doesn't support this directly, so we handle it manually below
        value.forEach((item) => {
          if (item !== null && item !== undefined) {
            params.append(`${key}[]`, String(item));
          }
        });
      } else {
        // Default: comma-separated format: key=val1,val2,val3
        const arrayString = value
          .filter((item) => item !== null && item !== undefined)
          .map((item) => String(item))
          .join(',');
        if (arrayString.length > 0) {
          params.append(key, arrayString);
        }
      }
      return;
    }

    // Handle strings
    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      // Skip empty strings if configured
      if (trimmedValue.length === 0 && skipEmptyString) return;
      params.append(key, trimmedValue);
      return;
    }

    // Handle numbers (including edge cases)
    if (typeof value === 'number') {
      if (Number.isNaN(value)) {
        if (!skipNull) params.append(key, 'NaN');
      } else if (!Number.isFinite(value)) {
        if (!skipNull) params.append(key, value > 0 ? 'Infinity' : '-Infinity');
      } else {
        params.append(key, String(value));
      }
      return;
    }

    // Handle booleans
    if (typeof value === 'boolean') {
      params.append(key, String(value));
      return;
    }

    // Handle Date objects
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        // Invalid date
        if (!skipNull) params.append(key, '');
      } else {
        params.append(key, value.toISOString());
      }
      return;
    }

    // Handle plain objects by JSON stringifying
    if (typeof value === 'object' && value !== null) {
      try {
        const jsonString = JSON.stringify(value);
        params.append(key, jsonString);
      } catch (error) {
        // Skip values that can't be stringified (circular references, etc.)
        console.warn(`Failed to stringify value for key "${key}":`, error);
      }
    }
  });

  return params.toString();
};

export const parseJwt = (token: string) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url?.replace(/-/g, '+')?.replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => {
        return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
      })
      .join(''),
  );

  return JSON.parse(jsonPayload);
};

export const dropdownStyle: React.CSSProperties = {
  boxShadow:
    '0 12px 28px 0 rgba(0, 0, 0, 0.1),0 2px 4px 0 rgba(0, 0, 0, 0.2),inset 0 0 0 1px rgba(255, 255, 255, 0.5)',
  borderRadius: 12,
  backgroundColor: 'var(--surface)',
  width: 245,
  maxHeight: 'calc(100vh - 85px)',
  zIndex: 999999999,
};
